import React from 'react';
import { Trophy, Shield, MapPin, School, BarChart3, Award } from 'lucide-react';

const ProfileStats = ({ athlete }) => {
    if (!athlete) return null;

    const stats = [
        { 
            label: 'Verification', 
            value: athlete.verified ? 'Verified' : 'Pending',
            icon: Shield,
            color: athlete.verified ? 'text-green-400' : 'text-orange-400'
        },
        { 
            label: 'Age Category', 
            value: athlete.age_category || '-',
            icon: Trophy,
            color: 'text-primary'
        },
        { 
            label: 'Sports', 
            value: athlete.sports?.length || 0,
            icon: Award,
            color: 'text-cyan-400'
        },
        { 
            label: 'Ranking', 
            value: athlete.ranking || 'Not Ranked',
            icon: BarChart3,
            color: 'text-yellow-400'
        }
    ];

    return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.map((stat, index) => (
                <div 
                    key={index} 
                    className="bg-[#0a1628] border border-white/5 p-4"
                    data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                    <div className="flex items-center gap-2 mb-2">
                        <stat.icon className={`w-4 h-4 ${stat.color}`} />
                        <span className="text-xs text-muted-foreground uppercase">{stat.label}</span>
                    </div>
                    <p className={`text-lg font-bold ${stat.color}`}>{stat.value}</p>
                </div>
            ))}
        </div>
    );
};

const ProfileDetailsView = ({ athlete }) => {
    if (!athlete) return null;

    return (
        <div className="space-y-6">
            {/* Stats Cards */}
            <ProfileStats athlete={athlete} />

            {/* Personal Information */}
            <div className="bg-[#0d2847] border border-white/5 p-6">
                <h3 className="font-heading font-bold text-sm uppercase mb-4 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-primary" />
                    Personal Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InfoRow label="Full Name" value={athlete.full_name} />
                    <InfoRow label="Date of Birth" value={athlete.date_of_birth} />
                    <InfoRow label="Country" value={athlete.country} />
                    <InfoRow label="County" value={athlete.county} />
                    <InfoRow label="Sub-county" value={athlete.subcounty} />
                    <InfoRow label="City" value={athlete.city} />
                    <InfoRow label="Mobile" value={athlete.mobile_number} />
                    <InfoRow label="Email" value={athlete.email} />
                </div>
            </div>

            {/* Affiliation */}
            <div className="bg-[#0d2847] border border-white/5 p-6">
                <h3 className="font-heading font-bold text-sm uppercase mb-4 flex items-center gap-2">
                    <School className="w-4 h-4 text-primary" />
                    Affiliation
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InfoRow label="School" value={athlete.school} />
                    <InfoRow label="Academy" value={athlete.academy} />
                </div>
            </div>

            {/* Sports */}
            {athlete.sports?.length > 0 && (
                <div className="bg-[#0d2847] border border-white/5 p-6">
                    <h3 className="font-heading font-bold text-sm uppercase mb-4 flex items-center gap-2">
                        <Trophy className="w-4 h-4 text-primary" />
                        Sports ({athlete.sports.length})
                    </h3>
                    <div className="flex flex-wrap gap-2">
                        {athlete.sports.map((sport, idx) => (
                            <span 
                                key={idx}
                                className="px-3 py-1 bg-primary/10 border border-primary/30 text-primary text-sm"
                            >
                                {sport}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {/* Bio */}
            {athlete.bio && (
                <div className="bg-[#0d2847] border border-white/5 p-6">
                    <h3 className="font-heading font-bold text-sm uppercase mb-4">Bio</h3>
                    <p className="text-muted-foreground whitespace-pre-wrap">{athlete.bio}</p>
                </div>
            )}
        </div>
    );
};

const InfoRow = ({ label, value }) => (
    <div className="space-y-1">
        <p className="text-xs text-muted-foreground uppercase">{label}</p>
        <p className="text-sm">{value || '-'}</p>
    </div>
);

export { ProfileStats, ProfileDetailsView, InfoRow };
export default ProfileDetailsView;
